import { QueryClient, useMutation } from '@tanstack/react-query';

import { ApprovalStatus, TApproval, TApprovalUserEntry, TCreateApprovalReturn, TUpdateApprovalReturn } from './types';
import { useContext } from 'react';
import { ApiContext } from '@shared/contexts';

import { TItemType, TVersionInfo } from '@modules/items';
import { useSearchParams } from '@shared/hooks';
import { TFileVersion, TUseFileResult } from '@modules/files';
import { getQueryKey } from '@shared/helpers';
import { TInitialApprovalUsers, TUseDocumentResult, TVersion } from '@modules/documents';
import invalidateFolderCardStatusQueries from '@modules/items/api/folderCardStatusQueries';

// --- HELPER:
const updateApprovalQueryData = (
  queryClient: QueryClient,
  itemId: string,
  itemType: TItemType,
  updatedApproval: TApproval,
  shouldReplaceApproval: boolean,
  updatedVersion?: TVersion | TFileVersion
) => {
  // update version info:
  if (shouldReplaceApproval)
    queryClient.setQueryData(
      [`version-info-${itemId}`],
      (oldData: TVersionInfo | undefined): TVersionInfo | undefined =>
        oldData
          ? {
              ...oldData,
              approvals: oldData.approvals.map((a) => (a._id === updatedApproval._id ? updatedApproval : a)),
            }
          : oldData
    );
  else
    queryClient.setQueryData(
      [`version-info-${itemId}`],
      (oldData: TVersionInfo | undefined): TVersionInfo | undefined =>
        oldData ? { ...oldData, approvals: [...oldData.approvals, updatedApproval] } : oldData
    );

  // update item:
  const updateApprovals = (approvals: TApproval[]) => {
    if (shouldReplaceApproval) return approvals.map((a) => (a._id === updatedApproval._id ? updatedApproval : a));
    return [...approvals, updatedApproval];
  };
  const updateVersions = (versions: (TVersion | TFileVersion)[]) =>
    updatedVersion ? versions.map((v) => (v.id === updatedApproval.versionId ? updatedVersion : v)) : versions;

  if (itemType === 'DOCUMENT') {
    return queryClient.setQueryData(
      [`document-${itemId}`],
      (oldData: TUseDocumentResult | undefined): TUseDocumentResult | undefined => {
        return oldData
          ? {
              ...oldData,
              doc: {
                ...oldData.doc,
                approvals: updateApprovals(oldData.doc.approvals),
                versions: updateVersions(oldData.doc.versions) as TVersion[],
              },
            }
          : oldData;
      }
    );
  }
  if (itemType === 'FILE')
    return queryClient.setQueryData(
      [`file-${itemId}`],
      (oldData: TUseFileResult | undefined): TUseFileResult | undefined =>
        oldData
          ? {
              ...oldData,
              file: {
                ...oldData.file,
                approvals: updateApprovals(oldData.file.approvals),
                versions: updateVersions(oldData.file.versions) as TFileVersion[],
              },
            }
          : oldData
    );
};

const cancelApprovalQueries = async (queryClient: QueryClient, itemId: string, itemType: TItemType) => {
  await queryClient.cancelQueries({ queryKey: [getQueryKey(itemType, itemId)] });
  await queryClient.cancelQueries({ queryKey: [`version-info-${itemId}`] });
};

// --- POST:

export const useCreateApproval = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (variables: {
    id: string;
    itemType: TItemType;
    versionId: string;
    authors?: TApprovalUserEntry[];
    reviewers?: TApprovalUserEntry[];
    approvers?: TApprovalUserEntry[];
    violationNote?: string;
    jsFile?: File;
  }): Promise<TCreateApprovalReturn> => {
    const formData = new FormData();

    const json = JSON.stringify({
      versionId: variables.versionId,
      authors: variables.authors,
      reviewers: variables.reviewers,
      approvers: variables.approvers,
      violationNote: variables.violationNote,
    });

    formData.append('json', json);
    if (variables.jsFile) {
      formData.append('file', variables.jsFile, variables.jsFile.name);
    }

    const res = await apiClient.post(`/approvals/${variables.id}/${variables.itemType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return res.data;
  };

  return useMutation({
    mutationFn,
    onSuccess: ({ approval, version }, variables) => {
      show('success', 'Created approval request.');
      updateApprovalQueryData(queryClient, variables.id, variables.itemType, approval, false, version);
      invalidateFolderCardStatusQueries(queryClient, variables.id, variables.itemType);
    },
    onError: handleApiError('Creating approval request failed!'),
  });
};

// --- PUT:

// helper function to update approval query data
const applyApprovalSuccess = async (
  queryClient: QueryClient,
  change: (key: string, value: string) => void,
  itemId: string,
  itemType: TItemType,
  approval: TApproval
) => {
  invalidateFolderCardStatusQueries(queryClient, itemId, itemType);
  if (approval.status === ApprovalStatus.APPROVED) {
    /**
     * version fully approved -> invalidate both item (doc/file/replica) and version-info query cache
     * reason: a lot of data related to the new version must be updated
     */
    queryClient.invalidateQueries({ queryKey: [getQueryKey(itemType, itemId)] });
    queryClient.invalidateQueries({ queryKey: [`version-info-${itemId}`] });
    change('view', approval.versionId);
  } else {
    // only the approval object updated -> update both item and version-info query cache
    updateApprovalQueryData(queryClient, itemId, itemType, approval, true);
  }
};

// update approval api hook
export const useUpdateApproval = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);
  const { change } = useSearchParams();

  const mutationFn = async ({
    id,
    itemType,
    ...body
  }: {
    id: string;
    itemType: TItemType;
    approvalId: string;
    authors?: TApprovalUserEntry[];
    reviewers?: TApprovalUserEntry[];
    approvers?: TApprovalUserEntry[];
  }): Promise<TUpdateApprovalReturn> =>
    await apiClient.put(`/approvals/${id}/${itemType}`, body).then((res) => res.data);

  return useMutation({
    mutationFn,
    onMutate: async (variables) => {
      await cancelApprovalQueries(queryClient, variables.id, variables.itemType);
    },
    onSuccess: async (data, variables) => {
      show('success', 'Updated approval request.');
      applyApprovalSuccess(queryClient, change, variables.id, variables.itemType, data.approval);
    },
    onError: handleApiError('Updating approval request failed!'),
  });
};

// sign approval api hook
export const useSignApproval = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);
  const { change } = useSearchParams();

  const mutationFn = async ({
    id,
    itemType,
    ...body
  }: {
    id: string;
    itemType: TItemType;
    approvalId: string;
    action: ApprovalStatus.AUTHORING | ApprovalStatus.REVIEWING | ApprovalStatus.APPROVING;
    accept: boolean;
    note?: string;
  }): Promise<TUpdateApprovalReturn> =>
    await apiClient.put(`/approvals/${id}/${itemType}/sign`, body).then((res) => res.data);

  return useMutation({
    mutationFn,
    onMutate: async (variables) => {
      await cancelApprovalQueries(queryClient, variables.id, variables.itemType);
    },
    onSuccess: async (data, variables) => {
      show('success', 'Your decision has been logged.');
      applyApprovalSuccess(queryClient, change, variables.id, variables.itemType, data.approval);
    },
    onError: handleApiError(), // default message
  });
};

// --- DELETE:

export const useCancelApproval = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (variables: { id: string; itemType: TItemType; approvalId: string }): Promise<TApproval> =>
    await apiClient
      .delete(`/approvals/${variables.id}/${variables.itemType}?approval=${variables.approvalId}`)
      .then((res) => res.data);

  return useMutation({
    mutationFn,
    onMutate: async (variables) => {
      await cancelApprovalQueries(queryClient, variables.id, variables.itemType);
    },
    onSuccess: (data, variables) => {
      show('success', 'Canceled approval request.');
      updateApprovalQueryData(queryClient, variables.id, variables.itemType, data, true);
      invalidateFolderCardStatusQueries(queryClient, variables.id, variables.itemType);
    },
    onError: handleApiError('Canceling approval request failed!'),
  });
};

// --- INITIAL APPROVAL USERS:

export const useUpdateInitialApprovalUsers = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (variables: {
    id: string;
    itemType: TItemType;
    initialApprovalUsers: TInitialApprovalUsers;
  }): Promise<void> => {
    await apiClient.put(
      `/approvals/${variables.id}/${variables.itemType}/initial-approval-users`,
      variables.initialApprovalUsers
    );
  };

  return useMutation({
    mutationFn,
    onSuccess: (_, variables) => {
      show('success', 'Original approval saved.');
      queryClient.invalidateQueries({ queryKey: [getQueryKey(variables.itemType, variables.id)] });
    },
    onError: handleApiError('Saving original approval failed!'),
  });
};

// --- NOT USED YET:
// export const useWaitForPdfBlob = (
//   itemType: 'FILE' | 'DOCUMENT',
//   itemId: string,
//   versionId: string | undefined,
//   enabled: boolean,
//   setEnabled: (enabled: boolean) => void,
//   onSuccess: (data: TVersion | TFileVersion) => void
// ) => {
//   const { apiClient, queryClient, retry, show } = useContext(ApiContext);

//   const itemTypeLower = itemType.toLocaleLowerCase();

//   const queryFn = useCallback(async (): Promise<void> => {
//     if (!versionId) return;

//     const { data } = await apiClient.get<TUseFileResult | TUseDocumentResult>(`/${itemTypeLower}s/${itemId}`);

//     const doc = (data as TUseDocumentResult).doc,
//       file = (data as TUseFileResult).file;
//     const item = doc || file;
//     const itemKey = doc ? 'doc' : 'file';

//     const version = item.versions.find((v) => v.id === versionId);
//     if (!version?.isPdfUpToDate) throw new Error('PDF not ready');

//     // Update the global item cache. See useDocument and useFile for the query keys.
//     queryClient.setQueryData(
//       [`${itemTypeLower}-${itemId}`],
//       (oldData: TUseFileResult | TUseDocumentResult | undefined): TUseFileResult | TUseDocumentResult | undefined =>
//         oldData && {
//           ...oldData,
//           [itemKey]: {
//             ...item,
//             versions: item.versions.reverse().map((v) => (v.id === versionId ? version : v)),
//           },
//         }
//     );

//     setEnabled(false); // stop polling
//     onSuccess(version); // call the success handler
//   }, [apiClient, itemTypeLower, itemId, versionId]);

//   const { isFetching, isError, error } = useQuery({
//     retry,
//     enabled,
//     refetchInterval: 3000,
//     refetchIntervalInBackground: true,
//     queryKey: [`wait-pdf-${itemTypeLower}-${itemId}-${versionId}`],
//     queryFn,
//   });

//   // Handle error
//   useEffect(() => {
//     if (isError && error) {
//       setEnabled(false);
//       show('info', 'PDF generation still in progress. Please try again shortly.');
//     }
//   }, [isError, error]);

//   return { isFetching };
// };
